import { useCallback, useEffect, useMemo, useState } from "react";

import { type Chart, type ChartDataset, type ChartOptions } from "chart.js";
import { motion } from "framer-motion";
import { omit } from "lodash";
import moment from "moment";
import { Line } from "react-chartjs-2";

import { LoadingSlide } from "@components/title-slide-view";
import { useUser } from "@components/user-context";
import { drawAlternativeMetricTitle } from "@features/sales-v3/utils/canvas";
import { buildLegend } from "@features/sales-v3/utils/tooltip";
import { FETCH_FORECAST } from "@gql/mmm-scenario-modeling";
import { getLighterColor } from "@hooks/use-design-element-manager/design-element-utils";
import {
  MetricFormatters,
  type MMMForecastDataInput,
  type Forecast,
  type ForecastVariables,
  type FetchCustomScenarioData_me_mmm_customScenarioData,
  type Forecast_me_mmm_forecast_forecastedContributionsByKpi,
} from "@north-beam/nb-client-side";
import { type BudgetMixContributions } from "@north-beam/nb-common";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";

import { type Kpi, KpiType } from "../../client-setting/types";
import { type PredictionData } from "../types";
import { useCustomScenarioData } from "../use-custom-scenario-data";

import { chartTooltip } from "./tooltip";

interface DataPoint {
  x: moment.Moment;
  y: number;
}

function convertToLabel(date: string) {
  const start = moment(date);
  const startFormatted = start.isValid() ? start.format("MMM DD") : "Start";
  const end = start.clone().add(6, "days");
  const endFormatted = end.isValid() ? end.format("MMM DD") : "End";
  return `${startFormatted} - ${endFormatted}`;
}

const PRIMARY_COLOR = "#7081FF";

const SPEND_LEGEND = buildLegend(
  {
    borderColor: PRIMARY_COLOR,
    backgroundColor: PRIMARY_COLOR,
    borderDash: [6, 2],
  },
  false,
);

interface Props {
  selectedKpi: Kpi;
  prediction: PredictionData | null;
  setPrediction: (predictions: PredictionData | null) => void;
  selectedAddToMixesRow: FetchCustomScenarioData_me_mmm_customScenarioData | null;
}

export function forecastedContributionsByKpiToMetadata(
  forecastedContributions:
    | Forecast_me_mmm_forecast_forecastedContributionsByKpi[]
    | null,
) {
  return (
    forecastedContributions?.reduce((acc, curr) => {
      acc[curr.kpi_name] = omit(curr, ["kpi_name", "__typename"]);
      return acc;
    }, {} as Record<string, BudgetMixContributions>) ?? {}
  );
}

export const ForecastChart = ({
  selectedKpi,
  prediction,
  setPrediction,
  selectedAddToMixesRow,
}: Props) => {
  const { user } = useUser();
  const { selectedCycle } = useSelectedCycle();
  const [isForecastLoading, setIsForecastLoading] = useState(false);
  const { customScenarioData, isCreateCustomScenarioDataLoading } =
    useCustomScenarioData();

  const isValueTypeRevenue = selectedKpi.kpi_type === KpiType.REVENUE;
  const kpiTypeDisplayName = isValueTypeRevenue ? "Revenue" : "Transaction";
  const loadForecast = useCallback(
    async ({
      modelPath,
      modelId,
      data,
    }: {
      modelPath: string;
      modelId?: number;
      data: MMMForecastDataInput[];
    }) => {
      return await user.apolloClient.query<Forecast, ForecastVariables>({
        query: FETCH_FORECAST,
        // fetchPolicy: "cache-first",
        variables: {
          input: { modelPath, modelId, data },
        },
      });
    },
    [user.apolloClient],
  );

  useEffect(() => {
    if (!selectedAddToMixesRow) {
      setPrediction(null);
      return;
    }

    (async () => {
      setIsForecastLoading(true);

      const data: MMMForecastDataInput[] = [];
      selectedAddToMixesRow.data.forEach((row) => {
        const inputElData: Record<string, number> = {};
        row.forEach((el, i) => {
          if (i === 0) {
            return;
          }
          inputElData[el.id] = Number(el.value);
        });
        data.push({ date: row[0].value, data: inputElData });
      });

      const forecast = await loadForecast({
        modelPath: selectedCycle.model.model_path,
        data,
        modelId: selectedCycle.model.create_northbeam_mmm_run_result,
      });

      setPrediction({
        file: selectedAddToMixesRow.name,
        data: forecast.data.me.mmm.forecast.prediction,
        create_budget_recommendations_metadata: {
          contribution_data: forecastedContributionsByKpiToMetadata(
            forecast.data.me.mmm.forecast.forecastedContributionsByKpi,
          ),
        },
      });
      setIsForecastLoading(false);
    })();
  }, [
    customScenarioData,
    selectedCycle,
    selectedAddToMixesRow,
    setPrediction,
    loadForecast,
  ]);

  const chartData = useMemo(() => {
    if (!prediction) {
      return { labels: [], datasets: [] };
    }

    const labels = prediction.data.map((e) => convertToLabel(e.date));
    const datasets: ChartDataset<"line", DataPoint[]>[] = [];

    const data: any = [];

    prediction.data.forEach((datum) => {
      data.push({
        x: convertToLabel(datum.date),
        y: datum.kpis[selectedKpi.kpi_name].value,
      });
    });

    datasets.push(
      {
        data,
        label: selectedKpi.friendly_name,
        series: prediction.file,
        yAxisID: "y",
        animation: false,
        pointRadius: 0,
        pointHitRadius: 50,
        hoverBackgroundColor: PRIMARY_COLOR,
        borderColor: PRIMARY_COLOR,
        hoverBorderColor: PRIMARY_COLOR,
      } as ChartDataset<"line", DataPoint[]>,
      {
        label: "Low",
        series: prediction.file,
        data: prediction.data.map((datum) => {
          return {
            x: convertToLabel(datum.date),
            y: datum.kpis[selectedKpi.kpi_name].low,
          };
        }) as any[],
        backgroundColor: getLighterColor(PRIMARY_COLOR, "0.11"),
        fill: "+1",
        yAxisID: "y",
        pointRadius: 0,
        borderWidth: 0,
        tension: 0,
        animation: false,
      } as ChartDataset<"line", DataPoint[]>,
      {
        label: "High",
        series: prediction.file,
        data: prediction.data.map((datum) => {
          return {
            x: convertToLabel(datum.date),
            y: datum.kpis[selectedKpi.kpi_name].high,
          };
        }) as any[],
        yAxisID: "y",
        pointRadius: 0,
        borderWidth: 0,
        tension: 0,
        animation: false,
      } as ChartDataset<"line", DataPoint[]>,
    );

    const uploadedFile = customScenarioData.find(
      (e) => e.name === prediction.file,
    );

    if (uploadedFile) {
      const spendData: any = [];

      uploadedFile.data.forEach((datum) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const spendDate = datum.find((e) => e.id === "date")!.value;
        const spendValue = datum
          .filter((e) => e.id !== "date")
          .reduce((acc, e) => acc + Number(e.value), 0);
        spendData.push({ x: convertToLabel(spendDate), y: spendValue });
      });
      datasets.push({
        data: spendData,
        label: "Spend",
        series: prediction.file,
        yAxisID: "y1",
        animation: false,
        pointRadius: 0,
        pointHitRadius: 50,
        hoverBackgroundColor: PRIMARY_COLOR,
        borderColor: PRIMARY_COLOR,
        hoverBorderColor: PRIMARY_COLOR,
        borderDash: [6, 2],
      } as ChartDataset<"line", DataPoint[]>);
    }

    return { labels, datasets };
  }, [customScenarioData, prediction, selectedKpi]);

  const isRevenue = selectedKpi.kpi_type === KpiType.REVENUE;

  const isLoading =
    isForecastLoading || isCreateCustomScenarioDataLoading || !prediction;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {isLoading ? (
        <div className="h-[424px]">
          <LoadingSlide text="Forecasting your custom budgets, this could take a minute or two..." />
        </div>
      ) : (
        <>
          <div className="flex items-center justify-center gap-6 text-xxs font-bold">
            <label className="flex items-center gap-2 cursor-pointer">
              <div className="w-6 h-0.5 bg-cosmic"></div>
              Median {kpiTypeDisplayName}
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <div dangerouslySetInnerHTML={{ __html: SPEND_LEGEND }}></div>
              Spend
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <div className="h-4 w-4 bg-cosmic-4"></div>
              {kpiTypeDisplayName} range
            </label>
          </div>
          <div className="h-[400px]">
            <Line
              datasetIdKey="id"
              plugins={[
                {
                  id: "forecastChartYScaleTitles",
                  afterDraw(
                    chart: Chart,
                    _args: any,
                    options: { mainScaleTitle: string },
                  ) {
                    const {
                      ctx,
                      chartArea,
                      scales: { y1 },
                    } = chart;
                    drawAlternativeMetricTitle(ctx, options.mainScaleTitle, {
                      font: {
                        family: "Inter",
                        size: 12,
                        weight: "600",
                        style: "normal",
                        lineHeight: 16,
                      },
                      x: 8,
                      tickWidth: 12,
                      chart,
                      chartArea,
                      config: { baseBackgroundColor: PRIMARY_COLOR },
                    });

                    drawAlternativeMetricTitle(ctx, "SPEND", {
                      font: {
                        family: "Inter",
                        size: 12,
                        weight: "600",
                        style: "normal",
                        lineHeight: 16,
                      },
                      x: y1.right - 2,
                      tickWidth: -12,
                      chart,
                      chartArea,
                      config: {
                        baseBackgroundColor: PRIMARY_COLOR,
                        borderDash: [6, 2],
                      },
                    });
                  },
                },
              ]}
              options={
                {
                  responsive: true,
                  maintainAspectRatio: false,
                  parsing: false,
                  interaction: {
                    mode: "index",
                    intersect: false,
                    position: "average",
                  },
                  plugins: {
                    forecastChartYScaleTitles: {
                      mainScaleTitle: selectedKpi.friendly_name.toUpperCase(),
                    },
                    tooltip: {
                      enabled: false,
                      external: chartTooltip,
                      user,
                      isRevenue,
                    },
                    legend: { display: false },
                  },
                  layout: {
                    padding: { left: 8, right: 20 },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      grid: {
                        drawTicks: false,
                        drawBorder: false,
                      },
                      ticks: {
                        maxTicksLimit: 6,
                        padding: 18,
                        font: { size: 11 },
                        callback: (val: string | number) => {
                          if (selectedKpi.kpi_type === "price_value") {
                            return MetricFormatters.currency(
                              Number(val),
                              user.currencies.currentCurrency,
                              { precision: "approximate" },
                            );
                          } else {
                            return MetricFormatters.decimal(
                              Number(val),
                              user.currencies.currentCurrency,
                              { precision: "approximate" },
                            );
                          }
                        },
                      },
                    },
                    y1: {
                      position: "right",
                      beginAtZero: true,
                      grid: {
                        drawTicks: false,
                        drawBorder: false,
                        drawOnChartArea: false,
                      },
                      ticks: {
                        maxTicksLimit: 6,
                        padding: 18,
                        font: { size: 11 },
                        callback: (val: string | number) =>
                          MetricFormatters.currency(
                            Number(val),
                            user.currencies.currentCurrency,
                            { precision: "approximate" },
                          ),
                      },
                    },
                    x: {
                      grid: {
                        drawBorder: false,
                        drawTicks: false,
                        drawOnChartArea: false,
                      },
                      ticks: {
                        padding: 18,
                        autoSkip: true,
                        font: { size: 11 },
                      },
                    },
                  },
                } as ChartOptions<"line">
              }
              data={chartData}
            />
          </div>
        </>
      )}
    </motion.div>
  );
};
